
import React, { useEffect, useState } from "react";
import { useStoreActions } from "ezStore";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { Button, showSnackbar } from "@cofano/cck";
import { Box } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { AdminSettingsGroup, AdminSettingsGroupField, AdminSettingsPlugin, AdminSettingsPluginValue } from "view/admin/utils/admin.types";
import { convertSettingsToData } from "view/admin/utils/AdminUtils";

import { GetAdminSettingsDocument } from "__generated__/view/admin/graphql/admin.queries.generated";
import { UpdateAdminSettingsDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";

const useAdminSettings = () => {
	const authActions = useStoreActions((actions) => actions.session);

	const [ plugins, setPlugins ] = useState<AdminSettingsPlugin[]>([]);
	const [ groups, setGroups ] = useState<AdminSettingsGroup[]>([]);

	const { data, refetch } = useQuery(GetAdminSettingsDocument);
	const [ updateAdminSettings ] = useMutation(UpdateAdminSettingsDocument);

	const onPluginChange = (pluginKey: string, value: AdminSettingsPluginValue) => {
		setPlugins([ ...plugins ].map((it) => {
			if (it.key !== pluginKey) {
				return it;
			}

			return {
				...it,
				value: value !== null && value !== undefined ? value.toString() : undefined,
			};
		}));
	};

	const onFieldChange = (field: AdminSettingsGroup, fieldItem: AdminSettingsGroupField, enabled: boolean) => {
		setGroups([ ...groups ].map((it) => ({
			...it,
			fields:
				field.name === it.name
					? (it.fields ?? []).map((item) => ({
						...item,
						enabled: item.name === fieldItem.name ? enabled : item.enabled ?? false,
					}))
					: it.fields ?? [],
		})));
	};

	const onFieldAccessChange = (group: AdminSettingsGroup, field: AdminSettingsGroupField) => {
		setGroups((prevFields) => prevFields.map((category) => {
			if (category.name !== group.name) {
				return category;
			}

			const updatedFields = (category.fields ?? []).map((item) => {
				if (item.name === field.name) {
					return {
						...item,
						accessUserTypes: field.accessUserTypes ?? [],
					};
				}

				return item;
			});

			return {
				...category,
				fields: updatedFields,
			};
		}));
	};

	const changes = React.useMemo(() => {
		const isPluginChanged = (current: AdminSettingsPlugin) => {
			const orig = data?.adminSettings?.plugins?.find((plugin) => plugin.key === current.key);
			return JSON.stringify(current.value) !== JSON.stringify(orig?.value);
		};

		const isFieldChanged = (group: AdminSettingsGroup, field: AdminSettingsGroupField) => {
			const orig = data?.adminSettings?.fields
				?.find((f) => f.name === group.name)
				?.fields?.find((it) => it.name === field.name);

			return (
				JSON.stringify(field.enabled) !== JSON.stringify(orig?.enabled) ||
				JSON.stringify(field.accessUserTypes) !== JSON.stringify(orig?.accessUserTypes)
			);
		};

		return {
			plugins: [ ...plugins ]
				.filter((plugin) => isPluginChanged(plugin)),
			fields: [ ...groups ]
				.filter((group) => {
					const changes = (group.fields ?? []).filter((field) => isFieldChanged(group, field));
					return changes.length > 0;
				})
				.map((group) => ({
					...group,
					fields: (group.fields ?? []).filter((field) => isFieldChanged(group, field)),
				})),
		};
	}, [ data?.adminSettings, plugins, groups ]);

	const save = async () => {
		try {
			if (!data?.adminSettings) {
				return;
			}

			const payload = {
				...data?.adminSettings,
				plugins: plugins,
				fields: groups,
			};

			await updateAdminSettings({
				variables: {
					input: {
						plugins: payload.plugins.map((plugin) => ({
							key: plugin.key,
							value: String(plugin.value ?? ""),
						})),
						fields: payload.fields.map((field) => ({
							name: field.name,
							fields: field.fields?.map((fieldItem) => ({
								name: fieldItem.name,
								enabled: fieldItem.enabled ?? false,
								accessUserTypes: fieldItem.accessUserTypes ?? [],
							})) ?? [],
						})),
					},
				},
			});

			const response = await refetch();
			const freshSettings = response?.data?.adminSettings;

			if (!freshSettings) {
				return;
			}

			const mappedSettings = convertSettingsToData(freshSettings);

			authActions.setPlugins(mappedSettings);

			showSnackbar(
				<React.Fragment>
					<Box mb={1}>
						{intl.formatMessage({ id: "SUCCESSFUL_SAVED" })}
					</Box>
					<Button
						variant="outlined"
						color="success"
						icon={<RefreshIcon />}
						size="small"
						onClick={() => window.location.reload()}>
						{intl.formatMessage({ id: "UPDATES_REFRESH" })}
					</Button>
				</React.Fragment>,
				{
					variant: "success",
				},
			);
		} catch (error) {
			showSnackbar(
				intl.formatMessage({ id: "ERROR_SAVING" }, { defaultMessage: "Failed to save settings." }),
				{
					variant: "error",
				},
			);
		}
	};

	useEffect(() => {
		if (data?.adminSettings) {
			setPlugins(data.adminSettings.plugins ?? []);
			setGroups(data.adminSettings.fields ?? []);
		}
	}, [ data?.adminSettings?.timestamp ]);

	return {
		settings: data?.adminSettings ?? null,
		plugins,
		groups,
		onPluginChange,
		onFieldChange,
		onFieldAccessChange,
		changes,
		save,
	};
};

export default useAdminSettings;
